import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CalendarDays, ChevronDown, Clock, DoorOpen, Merge, Plus, Printer,
  Sparkles, Trash2, Undo2, User, Users,
} from "lucide-react";
import { api } from "./api";
import { useConfirmation } from "./Confirmation";
import {
  Badge, BoutonSombre, Case, ChampChoix, ChampMultiple, ChampTexte, Modale, Puces, Recherche, Section,
} from "./ui";
import PlanningAffiche from "./PlanningAffiche";
import ModaleFusion, { ModaleAnnulerFusion } from "./PlanningFusion";

const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const OUI_NON = ["", "Oui", "Non"];
const LIBELLES_ETAT = {
  OUVERT: "Ouvert", DERNIERES_PLACES: "Dernières places", COMPLET: "Complet", FERME: "Fermé",
};

/* « Accepte une inscription après la date limite ? » ne part plus avec le formulaire : la
   règle découle de « participe au spectacle » (le serveur la pose). L'écran envoyait
   toujours « Non » et un cours hors spectacle refusait les nouveaux élèves dès février. */
const vide = {
  nom: "", jour: "Lundi", debut: "", fin: "", salle: "", discipline: "", niveau: "",
  age_affiche: "", age_min: "", age_max: "", professeur: "", places_max: "", forfait: "", prix_forfait: "",
  costume: "Non", participation_spectacle: "Oui",
};

/* L'exception décidée par l'école sur la règle du spectacle (« Oui » / « Non »), vide sinon. */
const exceptionFevrier = (c) => (c.accepte_apres_fevrier_force ? (c.accepte_apres_fevrier || "") : "");

export default function Planning({ cible }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState("");
  const [info, setInfo] = useState("");
  const [q, setQ] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [voirInactifs, setVoirInactifs] = useState(false);
  const [edite, setEdite] = useState(null);
  const [creation, setCreation] = useState(null);
  const [glisse, setGlisse] = useState(null);
  const [affiche, setAffiche] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [alerte, setAlerte] = useState("");
  // Fusion de deux cours : `{ depuis: cours }` (ouverte depuis la fiche d'un cours) ou `{}`.
  const [fusion, setFusion] = useState(null);
  // Annuler une fusion : l'identifiant du cours fusionné (fermé) qui rouvre.
  const [annulation, setAnnulation] = useState("");
  // Les profs du menu Profs : un prof tout juste créé n'a encore aucun cours au planning,
  // il doit pourtant pouvoir être choisi.
  const [fichesProfs, setFichesProfs] = useState([]);

  const charger = useCallback(async () => {
    try { setData(await api("/planning")); } catch (e) { setErreur(e.message); }
  }, []);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    api("/profs").then((d) => setFichesProfs((d.profs || []).map((p) => p.prenom).filter(Boolean))).catch(() => {});
  }, []);

  // Arrivée depuis le tableau de bord : la fiche du cours s'ouvre directement, c'est là
  // que se prennent les décisions (fermer le cours, changer l'horaire, la salle).
  useEffect(() => {
    if (!cible || !data) return;
    const c = [...(data.jours || []).flatMap((j) => j.cours), ...(data.hors_jour || [])]
      .find((x) => x.id === cible);
    if (c) setEdite(c);
  }, [cible, data]);

  const enregistrer = async (id, champs) => {
    setErreur(""); setInfo(""); setAlerte("");
    try {
      const r = await api(`/planning/cours/${id}`, { method: "PATCH", body: JSON.stringify({ champs }) });
      // Le serveur enregistre même s'il y a chevauchement : on le dit tout de suite, sans
      // attendre que l'école repère le bandeau du haut.
      if (r?.conflits?.length) {
        setAlerte(`Attention, chevauchement : ${r.conflits.map((k) =>
          `${k.jour} — ${k.cours[0].nom} (${k.cours[0].horaire}) et ${k.cours[1].nom} (${k.cours[1].horaire}) : ${k.motif}`).join(" · ")}`);
      }
      await charger();
      return r;
    } catch (e) { setErreur(e.message); return null; }
  };

  const creer = async (form) => {
    // Un double clic ne doit pas créer deux cours : le bouton reste bloqué pendant l'envoi.
    if (envoi) return;
    setEnvoi(true);
    setErreur("");
    try {
      // Les nombres partent tels que saisis : le serveur refuse « abc » avec un message clair.
      await api("/planning/cours", { method: "POST", body: JSON.stringify({ ...form, prix_forfait: 0 }) });
      setCreation(null);
      setInfo(`Cours « ${form.nom} » créé.`);
      await charger();
    } catch (e) { setErreur(e.message); } finally { setEnvoi(false); }
  };

  const supprimer = async (id) => {
    setErreur("");
    try {
      await api(`/planning/cours/${id}`, { method: "DELETE" });
      setEdite(null);
      setInfo("Cours supprimé.");
      await charger();
    } catch (e) { setErreur(e.message); }
  };

  const deposer = async (jour) => {
    if (!glisse || glisse.jour === jour) return setGlisse(null);
    const ancien = glisse;
    setGlisse(null);
    const r = await enregistrer(ancien.id, { jour });
    if (r) setInfo(`« ${ancien.nom} » déplacé au ${jour.toLowerCase()}.`);
  };

  const filtre = useCallback((c) => {
    if (!voirInactifs && !(c.statut || "").toLowerCase().startsWith("actif")) return false;
    if (discipline && c.discipline !== discipline) return false;
    const cible = `${c.nom} ${c.professeur} ${c.salle} ${c.discipline} ${c.niveau} ${c.age_affiche}`.toLowerCase();
    return !q || cible.includes(q.toLowerCase());
  }, [q, discipline, voirInactifs]);

  const conflitsParCours = useMemo(() => {
    const m = {};
    (data?.conflits || []).forEach((k) => k.cours.forEach((c) => { m[c.id] = k; }));
    return m;
  }, [data]);

  if (erreur && !data) return <p className="text-sm" style={{ color: "#B91C1C" }} data-testid="planning-erreur">{erreur}</p>;
  if (!data) return <p className="text-sm text-[var(--muted)]">Chargement du planning…</p>;

  const t = data.totaux;
  // L'onglet Listes contient aussi des couples (« Miller - Claudio ») : on ne propose que
  // des professeurs seuls, qui se cochent ensuite à plusieurs.
  const profsSimples = [...new Set([...(data.professeurs || []), ...fichesProfs]
    .flatMap((p) => String(p).split(/\s*[,;/&]\s*|\s+-\s+/).map((x) => x.trim()))
    .filter(Boolean))].sort((a, b) => a.localeCompare(b));

  if (affiche) return <PlanningAffiche data={data} onFermer={() => setAffiche(false)} />;

  return (
    <div className="space-y-6" data-testid="planning">
      <div className="rounded-3xl px-6 py-5 flex flex-wrap items-center gap-6" style={{ background: "#0F1B2D" }}>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: "rgba(255,255,255,0.5)" }}>Semaine type</div>
          <div className="font-display text-2xl text-white mt-1">{t.actifs} cours actifs</div>
          <div className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.55)" }}>
            {t.inactifs} inactif(s) · {t.heures} h de cours par semaine
          </div>
        </div>
        <div className="flex gap-6">
          <Chiffre valeur={`${t.eleves}/${t.places}`} label="Places occupées" />
          <Chiffre valeur={`${t.remplissage} %`} label="Remplissage" accent />
          <Chiffre valeur={data.conflits.length} label="Chevauchements" alerte={data.conflits.length > 0} />
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <button className="btn-ghost" onClick={() => setAffiche(true)} data-testid="planning-affiche-ouvrir"
            style={{ background: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.45)", color: "#fff" }}>
            <Printer size={14} /> Planning à imprimer
          </button>
          <button className="btn-ghost" onClick={() => setFusion({})} data-testid="planning-fusion-ouvrir"
            style={{ background: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.45)", color: "#fff" }}>
            <Merge size={14} /> Fusionner deux cours
          </button>
          <BoutonSombre onClick={() => { setErreur(""); setCreation({ ...vide }); }} testid="planning-nouveau">
            <Plus size={14} /> Nouveau cours
          </BoutonSombre>
        </div>
      </div>

      {info && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }} data-testid="planning-info">{info}</div>}
      {alerte && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#B45309" }} data-testid="planning-alerte-conflit">{alerte}</div>}
      {erreur && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }} data-testid="planning-erreur">{erreur}</div>}

      {/* Un cours dont le jour n'est pas un jour de la semaine (« Lun », vide) n'entre dans
          aucune colonne : sans ce bloc, il restait invisible tout en étant sur le site. */}
      {(data.hors_jour || []).length > 0 && (
        <div className="rounded-2xl p-4" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }} data-testid="planning-a-corriger">
          <div className="flex items-center gap-2 text-sm font-semibold mb-3" style={{ color: "#B91C1C" }}>
            <AlertTriangle size={16} /> À corriger — {data.hors_jour.length} cours sans jour valable
          </div>
          <p className="text-xs mb-3" style={{ color: "#991B1B" }}>
            Leur jour n'est pas un jour de la semaine : ils n'apparaissent dans aucune colonne. Ouvre la carte et choisis le jour.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {data.hors_jour.map((c) => (
              <div key={c.id}>
                <div className="text-[10px] font-semibold mb-1" style={{ color: "#B91C1C" }}>Jour : « {c.jour || "vide"} »</div>
                <CarteCours cours={c} conflit={conflitsParCours[c.id]} onOuvrir={() => { setErreur(""); setEdite(c); }} onGlisse={setGlisse} />
              </div>
            ))}
          </div>
        </div>
      )}

      {data.conflits.length > 0 && (
        <div className="rounded-2xl p-4" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }} data-testid="planning-conflits">
          <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#B45309" }}>
            <AlertTriangle size={16} /> {data.conflits.length} chevauchement(s) à regarder
          </div>
          <ul className="mt-2 space-y-1 text-xs" style={{ color: "#92400E" }}>
            {data.conflits.slice(0, 6).map((k, i) => (
              <li key={i}>
                {k.jour} — {k.cours[0].nom} ({k.cours[0].horaire}) et {k.cours[1].nom} ({k.cours[1].horaire}) : {k.motif}
                {k.salle ? ` · ${k.salle}` : ""}{k.professeur ? ` · ${k.professeur}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Section
        titre="Grille de la semaine"
        aide="Glisse une carte dans une autre colonne pour changer son jour. Clique une carte pour tout modifier."
        testid="planning-grille"
        action={
          <div className="flex flex-wrap items-center gap-3">
            <Recherche valeur={q} onChange={setQ} placeholder="Cours, prof, salle…" testid="planning-recherche" largeur={250} />
            <span
              className="flex items-center gap-2 text-xs text-[var(--muted)] cursor-pointer select-none"
              onClick={() => setVoirInactifs(!voirInactifs)}
              data-testid="planning-voir-inactifs"
            >
              <Case cochee={voirInactifs} onChange={setVoirInactifs} testid="planning-case-inactifs" /> Voir les inactifs
            </span>
          </div>
        }
      >
        <Puces
          testid="planning-filtre-discipline"
          valeur={discipline}
          onChange={setDiscipline}
          options={[{ valeur: "", label: "Toutes disciplines", nb: data.totaux.cours, testid: "planning-discipline-toutes" },
            // Une discipline qui n'a plus que des cours fermés (Bien-être) ne s'affiche
            // pas : elle réapparaît en cochant « Voir les inactifs ».
            ...data.disciplines.map((d) => ({
              valeur: d, label: d, testid: `planning-discipline-${d.toLowerCase().replace(/\s+/g, "-")}`,
              nb: data.jours.reduce((n, j) => n + j.cours.filter(
                (c) => c.discipline === d
                  && (voirInactifs || (c.statut || "").toLowerCase().startsWith("actif"))).length, 0),
            })).filter((d) => d.nb > 0 || d.valeur === discipline)]}
        />

        <div className="flex gap-3 overflow-x-auto pb-2">
          {/* Le dimanche n'a pas de cours : sa colonne vide rétrécissait les autres (école,
              30.10.2026). Elle revient seule si un cours y est un jour placé. */}
          {data.jours.filter(({ jour, cours }) => jour !== "Dimanche" || cours.length > 0).map(({ jour, cours }) => {
            const liste = cours.filter(filtre);
            return (
              <div
                key={jour}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => deposer(jour)}
                className="min-w-[188px] flex-1 rounded-2xl p-2.5 transition-colors"
                style={{
                  background: glisse && glisse.jour !== jour ? "var(--primary-tint)" : "#FBFCFD",
                  border: `1px dashed ${glisse && glisse.jour !== jour ? "var(--primary)" : "var(--border)"}`,
                }}
                data-testid={`planning-colonne-${jour.toLowerCase()}`}
              >
                <div className="flex items-center justify-between px-1.5 pb-2">
                  <span className="text-xs font-semibold">{jour}</span>
                  <span className="text-[11px] text-[var(--muted)]">{liste.length}</span>
                </div>
                <div className="space-y-2">
                  {liste.length === 0 && <p className="px-1.5 py-6 text-[11px] text-[var(--muted)]">Aucun cours.</p>}
                  {liste.map((c) => (
                    <CarteCours
                      key={c.id}
                      cours={c}
                      conflit={conflitsParCours[c.id]}
                      onOuvrir={() => { setErreur(""); setEdite(c); }}
                      onGlisse={setGlisse}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {edite && (
        <ModaleCours
          cours={edite}
          salles={data.salles}
          disciplines={data.disciplines}
          professeurs={profsSimples}
          niveaux={data.niveaux || []}
          forfaits={data.forfaits || []}
          source={data.planning_source}
          erreur={erreur}
          onFermer={() => setEdite(null)}
          onEnregistrer={async (champs) => {
            const r = await enregistrer(edite.id, champs);
            if (r) { setEdite(null); setInfo(`« ${r.cours.nom} » enregistré.`); }
          }}
          onSupprimer={() => supprimer(edite.id)}
          onFusionner={() => { setFusion({ depuis: edite }); setEdite(null); }}
          onAnnulerFusion={(absorbe) => { setAnnulation(absorbe); setEdite(null); }}
        />
      )}

      {annulation && (
        <ModaleAnnulerFusion
          absorbe={annulation}
          onFermer={() => setAnnulation("")}
          onFait={async (r) => {
            setAnnulation("");
            setErreur(""); setAlerte("");
            setInfo(r.message);
            await charger();
          }}
        />
      )}

      {fusion && (
        <ModaleFusion
          tous={[...(data.jours || []).flatMap((j) => j.cours), ...(data.hors_jour || [])]}
          depuis={fusion.depuis}
          onFermer={() => setFusion(null)}
          onFait={async (r) => {
            setFusion(null);
            setErreur(""); setAlerte("");
            setInfo(r.message);
            await charger();
          }}
        />
      )}

      {creation && (
        <Modale titre="Nouveau cours" sous_titre="Il apparaîtra dans la grille et dans les inscriptions de la console." onFermer={() => setCreation(null)} testid="planning-modale-creation" large>
          <div className="grid md:grid-cols-2 gap-x-5">
            <ChampTexte label="Nom du cours" valeur={creation.nom} onChange={(v) => setCreation({ ...creation, nom: v })} testid="creation-nom" />
            <ChampChoix label="Jour" valeur={creation.jour} options={JOURS} onChange={(v) => setCreation({ ...creation, jour: v })} testid="creation-jour" />
            <ChampTexte label="Début (16:00)" valeur={creation.debut} onChange={(v) => setCreation({ ...creation, debut: v })} testid="creation-debut" />
            <ChampTexte label="Fin (17:00)" valeur={creation.fin} onChange={(v) => setCreation({ ...creation, fin: v })} testid="creation-fin" />
            <ChampListe label="Salle" valeur={creation.salle} options={data.salles}
              onChange={(v) => setCreation({ ...creation, salle: v })} testid="creation-salle" />
            <ChampListe label="Discipline" valeur={creation.discipline} options={data.disciplines}
              onChange={(v) => setCreation({ ...creation, discipline: v })} testid="creation-discipline" />
            <ChampProfs valeur={creation.professeur} options={profsSimples}
              onChange={(v) => setCreation({ ...creation, professeur: v })} testid="creation-professeur" />
            <ChampTexte label="Places max" type="number" valeur={creation.places_max} onChange={(v) => setCreation({ ...creation, places_max: v })} testid="creation-places" />
            <ChampTexte label="Âge affiché" valeur={creation.age_affiche} onChange={(v) => setCreation({ ...creation, age_affiche: v })} testid="creation-age" />
            <div className="grid grid-cols-2 gap-x-3">
              <ChampTexte label="Âge min" type="number" valeur={creation.age_min} onChange={(v) => setCreation({ ...creation, age_min: v })} testid="creation-age-min" />
              <ChampTexte label="Âge max" type="number" valeur={creation.age_max} onChange={(v) => setCreation({ ...creation, age_max: v })} testid="creation-age-max" />
            </div>
            <ChampListe label="Niveau" valeur={creation.niveau} options={data.niveaux || []}
              onChange={(v) => setCreation({ ...creation, niveau: v })} testid="creation-niveau" />
          </div>
          <div className="rounded-2xl p-4 mb-4" style={{ background: "#FBFCFD", border: "1px solid var(--border)" }}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)] mb-3">Facturation</div>
            <div className="grid md:grid-cols-2 gap-x-5">
              <ChampChoix
                label="Forfait"
                valeur={creation.forfait}
                options={[{ valeur: "", label: "Aucun — tarif à la durée" },
                  ...(data.forfaits || []).map((x) => ({ valeur: x.nom, label: `${x.nom} — ${x.prix} CHF` }))]}
                onChange={(v) => setCreation({ ...creation, forfait: v })}
                testid="creation-forfait"
              />
              <ChampChoix label="Participe au spectacle" valeur={creation.participation_spectacle} options={OUI_NON} onChange={(v) => setCreation({ ...creation, participation_spectacle: v })} testid="creation-spectacle" />
            </div>
            <p className="text-[11px] text-[var(--muted)] mt-1">
              Le costume et la règle du 1er février découlent de « participe au spectacle » ; le prix du forfait
              se règle dans Réglages. Sans forfait, la durée du cours (début–fin) doit avoir un tarif dans
              Réglages › Tarifs et forfaits : sinon la console refuse de créer un cours qu'elle ne saurait
              pas facturer.
            </p>
          </div>
          {/* Un refus du serveur (cours sans prix, horaire invalide…) se lit DANS la
              fenêtre : le bandeau de l'écran est caché derrière elle. */}
          {erreur && (
            <p className="text-sm mb-3 rounded-xl px-3 py-2" style={{ color: "#B91C1C", background: "#FEF2F2", border: "1px solid #FECACA" }}
              data-testid="creation-erreur">{erreur}</p>
          )}
          <div className="flex gap-2">
            <BoutonSombre onClick={() => creer(creation)} testid="creation-enregistrer" disabled={envoi}>
              {envoi ? "Création…" : "Créer le cours"}
            </BoutonSombre>
            <button onClick={() => setCreation(null)} className="text-sm text-[var(--muted)]" data-testid="creation-annuler">Annuler</button>
          </div>
        </Modale>
      )}
    </div>
  );
}

const Chiffre = ({ valeur, label, accent, alerte }) => (
  <div>
    <div className="font-display text-2xl leading-none" style={{ color: alerte ? "#FCA5A5" : accent ? "#7DE8ED" : "#fff" }}>{valeur}</div>
    <div className="text-[10px] uppercase tracking-[0.16em] mt-1.5" style={{ color: "rgba(255,255,255,0.5)" }}>{label}</div>
  </div>
);

/* Une carte = un cours. Tout ce qui pèse sur la facturation est visible sans ouvrir la fiche. */
function CarteCours({ cours, conflit, onOuvrir, onGlisse }) {
  const inactif = !(cours.statut || "").toLowerCase().startsWith("actif");
  const plein = cours.places_max && cours.occupe >= cours.places_max;
  return (
    <div
      draggable
      onDragStart={() => onGlisse(cours)}
      onDragEnd={() => onGlisse(null)}
      onClick={onOuvrir}
      data-testid={`planning-cours-${cours.id}`}
      className="bg-white rounded-xl p-3 border cursor-pointer transition-transform hover:-translate-y-0.5 hover:shadow-[var(--shadow-sm)]"
      style={{ borderColor: conflit ? "#FDE68A" : "var(--border)", opacity: inactif ? 0.55 : 1 }}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: "var(--primary)" }}>
        <Clock size={12} /> {cours.debut}–{cours.fin}
        {conflit && <AlertTriangle size={12} style={{ color: "#B45309", marginLeft: "auto" }} title={conflit.motif} />}
      </div>
      <div className="text-sm font-semibold leading-tight mt-1">{cours.nom}</div>
      <div className="text-[11px] text-[var(--muted)] mt-1 flex flex-wrap gap-x-2">
        {cours.salle && <span className="inline-flex items-center gap-1"><DoorOpen size={11} />{cours.salle}</span>}
        {cours.professeur && <span className="inline-flex items-center gap-1"><User size={11} />{cours.professeur}</span>}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold tabular-nums"
          style={{ color: plein ? "#B91C1C" : "var(--text)" }}>
          <Users size={11} /> {cours.occupe}/{cours.places_max || "—"}
        </span>
        {cours.nb_attente > 0 && <span className="text-[10px] text-[var(--muted)]">+{cours.nb_attente} attente</span>}
      </div>
      <div className="h-1 rounded-full mt-2" style={{ background: "#E2E8F0" }}>
        <div className="h-1 rounded-full" style={{
          width: `${Math.min(cours.remplissage, 100)}%`,
          background: cours.remplissage >= 100 ? "#DC2626" : cours.remplissage >= 50 ? "var(--primary)" : "#F59E0B",
        }} />
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        {inactif && <Pastille ton="#64748B">Inactif</Pastille>}
        {!inactif && cours.etat_places !== "OUVERT" && (
          <Pastille ton={cours.etat_places === "COMPLET" ? "#DC2626" : "#B45309"}>
            {LIBELLES_ETAT[cours.etat_places] || cours.etat_places}{cours.etat_force ? " · forcé" : ""}
          </Pastille>
        )}
        {cours.forfait && <Pastille ton="#7C3AED">Forfait {cours.prix_forfait || ""}</Pastille>}
        {(cours.participation_spectacle || "").toLowerCase().startsWith("oui") && <Pastille ton="#2563EB"><Sparkles size={9} /> Spectacle</Pastille>}
        {/* La console ne sait pas chiffrer ce cours : la fiche dit pourquoi. */}
        {!inactif && (cours.sans_prix || []).length > 0 && (
          <Pastille ton="#DC2626"><AlertTriangle size={9} /> Sans prix</Pastille>
        )}
      </div>
    </div>
  );
}

const Pastille = ({ ton, children }) => (
  <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
    style={{ background: `${ton}14`, color: ton, border: `1px solid ${ton}33` }}>
    {children}
  </span>
);

/* Une liste de valeurs connues, mais la saisie reste libre : une nouvelle salle ou une
   nouvelle discipline n'attend pas qu'un premier cours l'utilise. */
function ChampListe({ label, valeur, options, onChange, testid }) {
  const id = `liste-${testid}`;
  return (
    <label className="block mb-4">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">{label}</span>
      <input className="input-field mt-1" list={id} value={valeur || ""}
        onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder="Choisir ou écrire…" />
      <datalist id={id}>
        {(options || []).filter(Boolean).map((o) => <option key={o} value={o} />)}
      </datalist>
    </label>
  );
}

/* Les profs connus se cochent ; un autre prénom s'ajoute à la main. */
function ChampProfs({ valeur, options, onChange, testid }) {
  const [autre, setAutre] = useState("");
  const decouper = (v) => String(v || "").split(/\s*[,;/&]\s*|\s+-\s+/).map((x) => x.trim()).filter(Boolean);
  const choisis = decouper(valeur);
  const ajouter = () => {
    const nom = autre.trim();
    if (nom && !choisis.includes(nom)) onChange([...choisis, nom].join(" - "));
    setAutre("");
  };
  return (
    <div>
      <ChampMultiple
        label="Professeur(s)"
        valeur={valeur}
        options={[...new Set([...options, ...choisis])].sort((a, b) => a.localeCompare(b))}
        onChange={onChange}
        testid={testid}
        aide="Plusieurs professeurs possibles : ils se cochent et se décochent, l'ordre suit les clics."
      />
      <div className="flex gap-2 -mt-2 mb-4">
        <input className="input-field" value={autre} placeholder="Autre prénom…"
          onChange={(e) => setAutre(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ajouter(); } }}
          data-testid={`${testid}-autre`} />
        <button type="button" className="btn-ghost" onClick={ajouter} data-testid={`${testid}-ajouter`}>Ajouter</button>
      </div>
    </div>
  );
}

function ModaleCours({ cours, salles, disciplines, professeurs, niveaux = [], forfaits = [], source, erreur, onFermer, onEnregistrer, onSupprimer, onFusionner, onAnnulerFusion }) {
  const { demander } = useConfirmation();
  const [f, setF] = useState({ ...cours, accepte_apres_fevrier: exceptionFevrier(cours) });
  const [confirme, setConfirme] = useState(false);
  const [impact, setImpact] = useState(null);
  // Les fusions encore en place qui touchent ce cours : on peut les annuler d'ici.
  const [fusions, setFusions] = useState([]);
  // « Fermer / supprimer » reste replié : on l'ouvre exprès, on ne tombe pas dessus en
  // cherchant « Enregistrer » en bas de la fiche (retour de l'école, 09.10.2026).
  const [zoneRetrait, setZoneRetrait] = useState(false);
  const maj = (k) => (v) => setF({ ...f, [k]: v });

  useEffect(() => {
    let vivant = true;
    api(`/planning/cours/${cours.id}/impact`)
      .then((d) => { if (vivant) setImpact(d); })
      .catch(() => {});
    api(`/planning/fusions?cours=${encodeURIComponent(cours.id)}`)
      .then((d) => { if (vivant) setFusions(d.items || []); })
      .catch(() => {});
    return () => { vivant = false; };
  }, [cours.id]);

  const champs = {};
  ["statut", "salle", "jour", "debut", "fin", "nom", "discipline", "niveau", "age_affiche",
    "age_min", "age_max", "professeur", "places_max", "commentaire_interne", "forfait",
    "participation_spectacle", "lien_whatsapp"].forEach((k) => {
    if (String(f[k] ?? "") !== String(cours[k] ?? "")) champs[k] = f[k];
  });
  if (String(f.accepte_apres_fevrier || "") !== exceptionFevrier(cours)) {
    champs.accepte_apres_fevrier = f.accepte_apres_fevrier || "";
  }
  const spectacle = (f.participation_spectacle || "").toLowerCase().startsWith("oui");
  const ouvertApresLimite = f.accepte_apres_fevrier ? f.accepte_apres_fevrier === "Oui" : !spectacle;

  // Garde-fou : fermer un cours le retire du site et des inscriptions. Jamais sans un oui.
  const fermerApresConfirmation = async (modifications) => {
    const ferme = String(modifications.statut || "").toLowerCase().startsWith("inactif");
    if (ferme && !await demander({
      titre: `Fermer le cours « ${cours.nom} » ?`,
      ton: "danger",
      texte: "Le cours disparaît du site et plus personne ne peut s'y inscrire.",
      points: [`${cours.nb_inscrits} élève(s) inscrit(s) et ${cours.nb_attente} en attente sont à prévenir par tes soins.`,
        "Les factures, l'historique et les attestations déjà établis restent intacts.",
        "Tu pourras le rouvrir en repassant son statut sur « Actif »."],
      valider: "Fermer le cours",
      annuler: "Le laisser ouvert",
    })) return;
    onEnregistrer(modifications);
  };

  // « Depuis la fiche de cours, un bouton qui ouvre la liste des élèves inscrits » (école,
  // 09.10.2026) : la feuille de présence de CE cours dans Liste (inscrits, essais, attente,
  // partis), la même que Liste → clic sur le cours. Une modification pas encore
  // enregistrée serait perdue : on le demande d'abord.
  const voirEleves = async () => {
    const n = Object.keys(champs).length;
    if (n && !await demander({
      titre: "Quitter la fiche sans enregistrer ?",
      texte: `${n} modification(s) de ce cours ne sont pas encore enregistrée(s).`,
      points: ["« Rester sur la fiche » puis « Enregistrer » pour les garder.",
        "« Voir les élèves » les abandonne et ouvre la liste du cours."],
      valider: "Voir les élèves",
      annuler: "Rester sur la fiche",
    })) return;
    window.dispatchEvent(new CustomEvent("console-aller", { detail: { onglet: "liste", id: cours.id } }));
  };

  return (
    <Modale titre={cours.nom || "Cours"} sous_titre={`${cours.id} · ${cours.nb_inscrits} inscrit(s), ${cours.nb_attente} en attente`} onFermer={onFermer} testid="planning-modale-cours" large>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-4" data-testid="cours-liste-eleves">
        <button type="button" className="btn-primary" onClick={voirEleves} data-testid="cours-voir-eleves">
          <Users size={15} /> Voir les élèves inscrits ({cours.nb_inscrits || 0})
        </button>
        <span className="text-[11px] text-[var(--muted)]">
          Ouvre la feuille de présence du cours (Liste) : inscrits, essais, liste d'attente, à imprimer.
        </span>
      </div>
      {/* Une fusion en place : le cours fermé dit où sont partis ses élèves, le cours gardé
          d'où ils viennent — et l'école peut revenir en arrière (09.10.2026). */}
      {fusions.map((x) => (
        <div key={x.id} className="rounded-2xl px-4 py-3 mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm"
          style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E3A8A" }} data-testid="cours-fusion-en-place">
          <Merge size={15} className="shrink-0" />
          <span className="flex-1 min-w-[220px]">
            {x.absorbe === cours.id
              ? <>Fusionné dans <strong>« {x.garde_nom} »</strong> le {String(x.le || "").slice(0, 10).split("-").reverse().join(".")} : ses {x.nb_eleves} élève(s) y sont passés.</>
              : <>A repris les {x.nb_eleves} élève(s) de <strong>« {x.absorbe_nom} »</strong> (fusion du {String(x.le || "").slice(0, 10).split("-").reverse().join(".")}).</>}
          </span>
          <button type="button" onClick={() => onAnnulerFusion(x.absorbe)} data-testid="cours-annuler-fusion"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white"
            style={{ border: "1.5px solid #1D4ED8", color: "#1D4ED8" }}>
            <Undo2 size={13} /> Annuler la fusion…
          </button>
        </div>
      ))}
      <div className="rounded-2xl p-4 mb-4" style={{ background: "#FBFCFD", border: "1px solid var(--border)" }}>
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)] mb-3 flex items-center gap-2"><CalendarDays size={12} /> Créneau</div>
        <div className="grid md:grid-cols-2 gap-x-5">
          {/* Un jour mal saisi (« Lun ») reste affiché pour qu'on voie quoi corriger. */}
          <ChampChoix label="Jour" valeur={f.jour} options={JOURS.includes(f.jour) ? JOURS : [f.jour || "", ...JOURS]} onChange={maj("jour")} testid="cours-jour" />
          <ChampListe label="Salle" valeur={f.salle} options={salles} onChange={maj("salle")} testid="cours-salle" />
          <ChampTexte label="Début" valeur={f.debut} onChange={maj("debut")} testid="cours-debut" />
          <ChampTexte label="Fin" valeur={f.fin} onChange={maj("fin")} testid="cours-fin" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-x-5">
        <ChampTexte label="Nom du cours" valeur={f.nom} onChange={maj("nom")} testid="cours-nom" />
        <ChampListe label="Discipline" valeur={f.discipline} options={disciplines} onChange={maj("discipline")} testid="cours-discipline" />
        <ChampProfs valeur={f.professeur} options={professeurs} onChange={maj("professeur")} testid="cours-professeur" />
        <ChampListe label="Niveau" valeur={f.niveau} options={niveaux} onChange={maj("niveau")} testid="cours-niveau" />
        <ChampTexte label="Âge affiché" valeur={f.age_affiche} onChange={maj("age_affiche")} testid="cours-age-affiche" />
        <div className="grid grid-cols-2 gap-x-3">
          <ChampTexte label="Âge min" type="number" valeur={f.age_min} onChange={maj("age_min")} testid="cours-age-min" />
          <ChampTexte label="Âge max" type="number" valeur={f.age_max} onChange={maj("age_max")} testid="cours-age-max" />
        </div>
        <ChampTexte label="Places max" type="number" valeur={f.places_max} onChange={maj("places_max")} testid="cours-places" aide={`${cours.occupe} place(s) occupée(s) aujourd'hui.`} />
        <ChampChoix label="Statut" valeur={f.statut} options={["Actif", "Inactif"]} onChange={maj("statut")} testid="cours-statut" />
      </div>

      <div className="rounded-2xl p-4 mb-4" style={{ background: "#FFF7FB", border: "1px solid #FBCFE8" }}>
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] mb-1" style={{ color: "#DB2777" }}>Ce que ce cours facture</div>
        <p className="text-[11px] mb-3" style={{ color: "#9D174D" }}>
          Ces deux réglages décident du montant porté sur la facture de chaque famille inscrite
          à ce cours, et de ce que le site autorise comme inscriptions.
        </p>
        {/* « On ne devrait pas avoir de cours sans prix » (école, 09.10.2026) : la console dit
            ici, avant toute inscription, qu'elle ne sait pas chiffrer ce cours, et pourquoi. */}
        {(cours.sans_prix || []).length > 0 && (
          <div className="text-[12px] mb-3 rounded-xl px-3 py-2 flex items-start gap-2"
            style={{ color: "#991B1B", background: "#FEF2F2", border: "1px solid #FECACA" }} data-testid="cours-sans-prix">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>
              <strong>La console ne sait pas calculer le prix de ce cours</strong> : {cours.sans_prix.join(" · ")}.
              Une famille qui s'y inscrirait aurait une facture bloquée (À traiter › « Cours sans tarif »).
              Ajoute le tarif dans Réglages › Tarifs et forfaits, ou choisis un forfait ci-dessous.
            </span>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-x-5">
          <ChampChoix
            label="Forfait"
            valeur={f.forfait}
            options={[{ valeur: "", label: "Aucun — prix calculé sur la durée du cours" },
              ...forfaits.map((x) => ({ valeur: x.nom, label: `${x.nom} — ${x.prix} CHF${x.costume_inclus ? ", costume compris" : ""}` }))]}
            onChange={maj("forfait")}
            testid="cours-forfait"
            aide="Avec un forfait, chaque élève de ce cours est facturé ce prix fixe. Sans forfait, le prix vient du barème à la durée (Réglages)."
          />
          <ChampChoix label="Participe au spectacle" valeur={f.participation_spectacle} options={OUI_NON} onChange={maj("participation_spectacle")} testid="cours-spectacle"
            aide="« Oui » ajoute le costume au semestre 2 (sauf s'il est compris dans le forfait) et ferme les inscriptions dès la date de bascule." />
          <ChampChoix label="Nouveaux élèves après la date limite" valeur={f.accepte_apres_fevrier}
            options={[{ valeur: "", label: `Selon le spectacle (${spectacle ? "non" : "oui"})` },
              { valeur: "Oui", label: "Oui — exception : toujours ouvert" },
              { valeur: "Non", label: "Non — exception : fermé dès la date limite" }]}
            onChange={maj("accepte_apres_fevrier")} testid="cours-accepte-apres-fevrier"
            aide="Laisse « Selon le spectacle » : la règle suit la case ci-contre et la date limite du spectacle (Réglages → Saison). Oui ou Non seulement pour une exception." />
        </div>
        <div className="text-[11px] mt-2 rounded-xl px-3 py-2" style={{ color: "#9D174D", background: "#FFEDF6" }} data-testid="cours-effet-facturation">
          <strong>Concrètement pour ce cours :</strong>{" "}
          {f.forfait
            ? <>chaque élève est facturé le forfait <strong>{f.forfait}</strong>
              {(() => { const x = forfaits.find((k) => k.nom === f.forfait); return x ? ` (${x.prix} CHF${x.costume_inclus ? ", costume compris" : ""})` : ""; })()}</>
            : <>le prix de chaque élève est calculé sur la durée du cours ({f.debut}–{f.fin}), selon le barème des Réglages</>}
          {". "}
          {spectacle
            ? <>Spectacle : oui — costume {cours.costume_calcule === "Oui" ? <strong>facturé en plus au semestre 2</strong> : <>déjà <strong>compris dans le forfait</strong></>},
              et {ouvertApresLimite
                ? <>plus aucun essai dès la date de bascule, mais <strong>les nouveaux élèves restent acceptés</strong> (exception).</>
                : <><strong>plus aucune inscription ni essai</strong> accepté dès la date de bascule.</>}</>
            : <>Spectacle : non — aucun costume facturé, et {ouvertApresLimite
                ? "les inscriptions restent ouvertes toute l'année."
                : <><strong>plus de nouvel élève dès la date limite</strong> (exception).</>}</>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-x-5 gap-y-3 mb-4">
        <Lecture label="Message affiché sur le site" valeur={cours.message_site || cours.message_site_auto} testid="cours-message-site" />
        <Lecture label="État des places" valeur={`${LIBELLES_ETAT[cours.etat_places] || cours.etat_places}${cours.etat_force ? " (forcé par l'école)" : " (calculé)"}`} testid="cours-etat-places" />
      </div>
      <ChampTexte label="Commentaire interne" valeur={f.commentaire_interne} onChange={maj("commentaire_interne")} testid="cours-commentaire" />

      {/* Le lien d'invitation du groupe part ensuite dans la confirmation de chaque famille :
          l'école n'ajoute plus personne à la main (demandé le 02.10.2026). */}
      <ChampTexte label="Lien du groupe WhatsApp" valeur={f.lien_whatsapp} onChange={maj("lien_whatsapp")}
        testid="cours-lien-whatsapp"
        aide="WhatsApp → Infos du groupe → Inviter via un lien → Copier le lien. Les familles inscrites à ce cours le recevront dans leur confirmation. Vide = rien n'est envoyé." />

      {/* Fusionner : deux cours qui manquent d'élèves n'en font plus qu'un. L'aperçu dit
          tout avant de toucher à quoi que ce soit (demande de l'école, octobre 2026). */}
      {(cours.statut || "").toLowerCase().startsWith("actif") && (
        <button type="button" onClick={onFusionner} data-testid="cours-fusionner"
          className="w-full mt-5 flex items-center gap-2 px-4 py-3 rounded-2xl text-left text-xs font-semibold"
          style={{ border: "1px dashed var(--border)", color: "var(--text)" }}>
          <Merge size={13} style={{ color: "var(--primary)" }} />
          Fusionner avec un autre cours…
          <span className="font-normal text-[var(--muted)]">— pas assez d'élèves : les réunir dans un seul cours</span>
        </button>
      )}

      <div className="rounded-2xl mt-3" style={{ border: "1px dashed #FCA5A5", background: zoneRetrait ? "#FFFBFB" : "transparent" }} data-testid="cours-fin-de-vie">
        <button type="button" onClick={() => setZoneRetrait((o) => !o)} aria-expanded={zoneRetrait}
          className="w-full flex items-center gap-2 px-4 py-3 text-left text-xs font-semibold" style={{ color: "#B91C1C" }}
          data-testid="cours-zone-retrait">
          <AlertTriangle size={13} />
          Fermer ou supprimer ce cours…
          <span className="font-normal text-[var(--muted)]">— le retire du site et des inscriptions</span>
          <ChevronDown size={14} className="ml-auto transition-transform" style={{ transform: zoneRetrait ? "rotate(180deg)" : "none" }} />
        </button>
        {zoneRetrait && <div className="px-4 pb-4">
        {!impact && <p className="text-xs text-[var(--muted)]">Lecture des conséquences…</p>}
        {impact && impact.supprimable && (
          <>
            <p className="text-xs text-[var(--muted)] mb-3">
              Ce cours n'a jamais servi : aucun inscrit, aucun essai, aucune facture. Il peut être supprimé.
            </p>
            {confirme ? (
              <span className="flex items-center gap-2">
                <span className="text-xs" style={{ color: "#B91C1C" }}>Supprimer définitivement ?</span>
                <BoutonSombre onClick={onSupprimer} testid="cours-supprimer-confirme" danger><Trash2 size={13} /> Oui, supprimer</BoutonSombre>
                <button onClick={() => setConfirme(false)} className="text-xs text-[var(--muted)]">Non</button>
              </span>
            ) : (
              <button onClick={() => setConfirme(true)} className="text-xs flex items-center gap-1.5" style={{ color: "#B91C1C" }} data-testid="cours-supprimer">
                <Trash2 size={13} /> Supprimer ce cours
              </button>
            )}
          </>
        )}
        {impact && !impact.supprimable && (
          <>
            <p className="text-xs mb-3" style={{ color: "#B45309" }}>
              <AlertTriangle size={12} className="inline mr-1" />
              Ce cours a déjà servi : {impact.raisons.join(" · ")}. On ne le supprime pas — on le
              <strong> ferme</strong> : il sort du site et des nouvelles inscriptions, l'historique, les
              factures et les attestations restent intacts.
            </p>
            <div className="grid sm:grid-cols-3 gap-3 mb-3">
              <Mesure valeur={impact.inscrits.length} label="élèves inscrits" />
              <Mesure valeur={impact.familles} label="familles à prévenir" />
              <Mesure valeur={`${impact.montant_en_jeu.toLocaleString("fr-CH")} CHF`} label="annoncés pour ce cours" />
            </div>
            {impact.inscrits.length > 0 && (
              <details data-testid="cours-impact-liste">
                <summary className="text-xs cursor-pointer" style={{ color: "var(--primary)" }}>
                  Voir qui est concerné ({impact.inscrits.length})
                </summary>
                <ul className="mt-2 space-y-1 text-[11px] text-[var(--muted)] max-h-40 overflow-auto">
                  {impact.inscrits.map((p) => (
                    <li key={p.inscription_id}>
                      <span className="text-[var(--text)]">{p.eleve}</span> — {p.responsable || "responsable inconnu"} · {p.email || "sans e-mail"}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {impact.essais_a_venir.length > 0 && (
              <p className="text-[11px] mt-2" style={{ color: "#B45309" }}>
                {impact.essais_a_venir.length} cours d'essai déjà réservé(s) à venir : à prévenir avant de fermer.
              </p>
            )}
            {impact.autres_cours.length > 0 && (
              <p className="text-[11px] text-[var(--muted)] mt-2">
                Où basculer les élèves dans la même discipline : {impact.autres_cours.filter((a) => a.libres > 0)
                  .map((a) => `${a.nom} (${a.jour} ${a.debut}, ${a.libres} place(s))`).join(" · ") || "aucun cours avec de la place"}.
              </p>
            )}
            <div className="mt-3">
              {/* Bouton « danger » au contour rouge : il ne ressemble jamais à « Enregistrer ». */}
              <button
                type="button"
                onClick={() => fermerApresConfirmation({ statut: "Inactif" })}
                data-testid="cours-fermer"
                disabled={(cours.statut || "").toLowerCase().startsWith("inactif")}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-white disabled:opacity-50"
                style={{ border: "1.5px solid #DC2626", color: "#B91C1C" }}
              >
                {(cours.statut || "").toLowerCase().startsWith("inactif") ? "Déjà fermé" : "Fermer le cours (Inactif)"}
              </button>
            </div>
          </>
        )}
        </div>}
      </div>

      {/* Enregistrer / Annuler : la dernière chose en bas de la fiche, là où l'on descend
          pour sauver — sous la zone « Fermer ou supprimer », repliée exprès. Pas collés à
          l'écran : ils passaient par-dessus les champs (école, 09.10.2026). */}
      {/* Refus du serveur (ex. la modification laisserait le cours sans prix) : dit ici,
          à côté de « Enregistrer », pas seulement dans le bandeau caché derrière la fiche. */}
      {erreur && (
        <p className="text-sm mt-4 rounded-xl px-3 py-2" style={{ color: "#B91C1C", background: "#FEF2F2", border: "1px solid #FECACA" }}
          data-testid="cours-erreur">{erreur}</p>
      )}
      <div className="mt-5 pt-4 border-t flex flex-wrap items-center gap-3" style={{ borderColor: "var(--border)" }}
        data-testid="cours-actions">
        <BoutonSombre onClick={() => fermerApresConfirmation(champs)} testid="cours-enregistrer" disabled={Object.keys(champs).length === 0}>
          Enregistrer {Object.keys(champs).length ? `(${Object.keys(champs).length})` : ""}
        </BoutonSombre>
        <button onClick={onFermer} className="text-sm text-[var(--muted)]" data-testid="cours-annuler">Annuler</button>
        {Object.keys(champs).length > 0 && (
          <span className="ml-auto text-[11px] text-[var(--muted)]" data-testid="cours-modifs">
            {Object.keys(champs).length} modification(s) pas encore enregistrée(s)
          </span>
        )}
      </div>
    </Modale>
  );
}

const Mesure = ({ valeur, label }) => (
  <div className="bg-white rounded-xl px-3 py-2 border" style={{ borderColor: "var(--border)" }}>
    <div className="font-display text-xl leading-none">{valeur}</div>
    <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--muted)] mt-1">{label}</div>
  </div>
);

/* Une information calculée : on la montre, on ne la saisit pas. */
const Lecture = ({ label, valeur, testid }) => (
  <div data-testid={testid}>
    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">{label}</span>
    <div className="mt-1 rounded-xl px-3 py-2 text-sm" style={{ background: "#F1F5F9", color: "#475569" }}>
      {valeur || "—"}
    </div>
  </div>
);
